import { useState, useEffect, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';

/**
 * Sync status returned from the API
 */
export interface SyncStatus {
  lastSyncCompleted: string | null;
  lastSyncDuration: number | null;
  buildingsProcessed: number | null;
  status: 'completed' | 'failed' | 'partial' | 'running';
  isStale: boolean;
  message: string;
  errorMessage?: string;
}

/**
 * Custom hook for fetching and managing building financials sync status
 *
 * Features:
 * - Fetches sync status from API on mount
 * - Auto-refreshes every 5 minutes
 * - Handles loading and error states
 * - Provides manual refetch capability
 *
 * @returns {object} Sync status data, loading state, and refetch function
 *
 * @example
 * ```tsx
 * const { syncStatus, isLoading, refetch } = useSyncStatus();
 *
 * if (syncStatus?.status === 'failed') {
 *   return <ErrorMessage>{syncStatus.errorMessage}</ErrorMessage>
 * }
 * ```
 */
export const useSyncStatus = () => {
  const { session } = useSession();
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSyncStatus = useCallback(async () => {
    if (!session) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/building-financials/sync-status`,
        { method: 'GET' },
        session
      );

      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data);
        setError(null);
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch sync status' }));
        throw new Error(errorData.message || 'Failed to fetch sync status');
      }
    } catch (err) {
      console.error('Failed to fetch sync status:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setSyncStatus(null);
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchSyncStatus();

    // Refresh every 5 minutes (300000ms)
    const interval = setInterval(fetchSyncStatus, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [fetchSyncStatus]);

  return {
    syncStatus,
    isLoading,
    error,
    refetch: fetchSyncStatus
  };
};
